import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { RefreshCw, Power, Star } from "lucide-react";
import { Card, CardContent } from "@/components/ui/Card.tsx";
import { Button } from "@/components/ui/Button.tsx";
import { Badge } from "@/components/ui/Badge.tsx";
import { Spinner } from "@/components/ui/Spinner.tsx";
import { adminApi, type AdminStockAvatar } from "@/api/admin.ts";

/**
 * Admin curation for the shared HeyGen stock avatar pool.
 *
 *   - "Sync from HeyGen" pulls the current catalogue (server-side); the button
 *     reports what changed.
 *   - Each row can be enabled/disabled (offered to agencies or not) and one row
 *     PER GENDER can be the house default the render fallback uses (a male default
 *     and a female default, so a presenter beat renders with a matching avatar).
 *
 * The heavy invariants live in the backend: one default per gender, a disabled
 * row can't be default, delisted rows aren't offered. This screen just drives them
 * and reflects the result the API returns.
 */
export function AdminStockAvatarsPage() {
  const qc = useQueryClient();
  const [syncResult, setSyncResult] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "stock-avatars"],
    queryFn: () => adminApi.listStockAvatars(),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "stock-avatars"] });
  const fail = (err: unknown, fallback: string) =>
    setActionError((err as { message?: string })?.message || fallback);

  const sync = useMutation({
    mutationFn: () => adminApi.syncStockAvatars(),
    onSuccess: (r) => {
      setActionError(null);
      setSyncResult(`Synced: ${r.upserted} upserted, ${r.delisted} delisted, ${r.fetched} fetched from HeyGen.`);
      invalidate();
    },
    onError: (err) => { setSyncResult(null); fail(err, "Sync failed"); },
  });

  const toggleEnabled = useMutation({
    mutationFn: (a: AdminStockAvatar) => adminApi.updateStockAvatar(a.stockId, { isEnabled: !a.isEnabled }),
    onSuccess: () => { setActionError(null); invalidate(); },
    onError: (err) => fail(err, "Could not update this avatar"),
  });

  const makeDefault = useMutation({
    mutationFn: (a: AdminStockAvatar) => adminApi.updateStockAvatar(a.stockId, { isDefault: true }),
    onSuccess: () => { setActionError(null); invalidate(); },
    onError: (err) => fail(err, "Could not set the default"),
  });

  const avatars = data?.avatars ?? [];
  const offered = avatars.filter((a) => a.isEnabled && !a.isDelisted).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold">Stock Avatars</h1>
          <p className="text-sm text-muted-foreground">
            The shared HeyGen pool offered to agencies. {offered} of {avatars.length} offered.
          </p>
        </div>
        <Button onClick={() => sync.mutate()} loading={sync.isPending}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Sync from HeyGen
        </Button>
      </div>

      {syncResult && (
        <Card><CardContent className="py-3 text-sm text-emerald-700">{syncResult}</CardContent></Card>
      )}
      {actionError && (
        <Card><CardContent className="py-3 text-sm text-red-700">{actionError}</CardContent></Card>
      )}

      {isLoading ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : avatars.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">
          No stock avatars yet. Run "Sync from HeyGen" to pull the catalogue.
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {avatars.map((a) => (
            <Card key={a.stockId} className={a.isEnabled && !a.isDelisted ? "" : "opacity-60"}>
              <CardContent className="flex items-center gap-4 py-3">
                {a.previewUrl ? (
                  <img
                    src={a.previewUrl}
                    alt={a.displayName ?? "stock avatar"}
                    className="h-12 w-12 shrink-0 rounded-md object-cover"
                  />
                ) : (
                  <div className="h-12 w-12 shrink-0 rounded-md bg-surface-100" />
                )}

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate font-medium">{a.displayName ?? a.providerAvatarId}</span>
                    {a.isDefault && (
                      <Badge variant="info">
                        {a.gender ? `default (${a.gender})` : "default"}
                      </Badge>
                    )}
                    {a.avatarType && <Badge variant="muted">{a.avatarType}</Badge>}
                    {a.gender && <Badge variant="muted">{a.gender}</Badge>}
                    {!a.isEnabled && <Badge variant="muted">disabled</Badge>}
                    {a.isDelisted && <Badge variant="warning">delisted</Badge>}
                  </div>
                  <div className="mt-0.5 truncate text-xs text-muted-foreground">{a.providerAvatarId}</div>
                </div>

                <div className="flex shrink-0 items-center gap-1.5">
                  {/* Set default: only for an enabled, non-delisted, non-default row —
                      the backend refuses a disabled default, so don't offer it. */}
                  <Button
                    variant="ghost"
                    size="sm"
                    title={a.isDefault ? "This is the house default" : "Make house default"}
                    disabled={a.isDefault || !a.isEnabled || a.isDelisted || makeDefault.isPending}
                    onClick={() => makeDefault.mutate(a)}
                  >
                    <Star className={a.isDefault ? "h-4 w-4 fill-current" : "h-4 w-4"} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    title={a.isEnabled ? "Disable (stop offering)" : "Enable (offer to agencies)"}
                    disabled={toggleEnabled.isPending}
                    onClick={() => toggleEnabled.mutate(a)}
                  >
                    <Power className={a.isEnabled ? "h-4 w-4 text-emerald-600" : "h-4 w-4"} />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
